import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";

const EVENT_DATE = new Date("2026-06-28T00:00:00");

const getTimeLeft = () => {
  const diff = EVENT_DATE - new Date();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const RSVP_COLLECTION = "baptism_rsvps";

const exportToCSV = (rsvps) => {
  if (rsvps.length === 0) return;
  const headers = [
    "Name",
    "Email",
    "Attending",
    "Session",
    "Guests",
    "Allergies",
    "Message",
    "Submitted At",
  ];
  const rows = rsvps.map((r) => [
    r.name,
    r.email || "",
    r.attending,
    r.attending === "no" ? "" : r.session || "both",
    r.attending === "no" ? 0 : r.guests,
    (r.allergy || "").replace(/,/g, ";"),
    (r.message || "").replace(/,/g, ";"),
    new Date(r.submittedAt).toLocaleString(),
  ]);
  const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `baptism_rsvps_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const getStats = (rsvps) => {
  const attending = rsvps.filter((r) => r.attending === "yes");
  const notAttending = rsvps.filter((r) => r.attending === "no");
  const maybe = rsvps.filter((r) => r.attending === "maybe");
  const totalGuests = attending.reduce(
    (sum, r) => sum + Number(r.guests || 1),
    0,
  );
  return {
    attending: attending.length,
    notAttending: notAttending.length,
    maybe: maybe.length,
    totalGuests,
  };
};

const G = "#c9a55a";
const NAVY = "#12122a";

function BaptismInvite() {
  const [rsvps, setRsvps] = useState([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPass, setAdminPass] = useState("");
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminError, setAdminError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  const [reminderStatus, setReminderStatus] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);

    const fetchRSVPs = async () => {
      try {
        const q = query(
          collection(db, RSVP_COLLECTION),
          orderBy("submittedAt", "asc"),
        );
        const snapshot = await getDocs(q);
        setRsvps(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            submittedAt:
              doc.data().submittedAt?.toDate?.().toISOString() ??
              new Date().toISOString(),
          })),
        );
      } catch (err) {
        console.error("Failed to load RSVPs:", err);
      }
    };
    fetchRSVPs();

    return () => clearInterval(timer);
  }, []);

  const stats = getStats(rsvps);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    setReminderStatus(null);
    try {
      const res = await fetch("/api/send-emails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "reminder", rsvps }),
      });
      const data = await res.json();
      setReminderStatus(
        data.sent === 0
          ? "No guests with email addresses found."
          : `Reminders sent to ${data.sent} guest${data.sent !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}.`,
      );
    } catch {
      setReminderStatus("Failed to send reminders. Check your email config.");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleAdminUnlock = () => {
    if (adminPass === "admin123") {
      setAdminUnlocked(true);
      setAdminError(false);
    } else setAdminError(true);
  };

  return (
    <div
      style={{
        background:
          "linear-gradient(160deg,#fdf8ef 0%,#fffcf6 50%,#fdf5e6 100%)",
        minHeight: "100vh",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
    >
      {/* Top gold rule */}
      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)`,
        }}
      />

      {/* ── HERO ── */}
      <div className="text-center pt-16 pb-10 px-6">
        {/* Cross SVG */}
        <div className="flex justify-center mb-10">
          <svg width="30" height="54" viewBox="0 0 30 54" fill="none">
            <rect x="13" y="0" width="4" height="54" rx="2" fill={G} />
            <rect x="0" y="14" width="30" height="4" rx="2" fill={G} />
            <circle
              cx="15"
              cy="16"
              r="4"
              fill="#fdf8ef"
              stroke={G}
              strokeWidth="1.5"
            />
          </svg>
        </div>

        <p
          style={{
            letterSpacing: "0.28em",
            color: G,
            fontFamily: "'Cormorant Garamond', serif",
          }}
          className="text-xs uppercase font-medium mb-6"
        >
          You are cordially invited to the Holy Baptism of
        </p>

        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            color: NAVY,
            lineHeight: 1.12,
          }}
          className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-1 px-2"
        >
          Munachimso Zoe
        </h1>
        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            color: NAVY,
            fontStyle: "italic",
            fontWeight: 400,
          }}
          className="text-4xl sm:text-5xl lg:text-6xl mb-10"
        >
          Maxwell
        </h1>

        {/* Baby photo */}
        <div className="flex justify-center mb-10">
          <div
            style={{
              border: `3px solid ${G}`,
              padding: 5,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.7)",
              boxShadow: `0 8px 32px ${G}30`,
            }}
          >
            <img
              src="https://res.cloudinary.com/dfsmwu4av/image/upload/a_auto_right.hflip,c_fit,q_auto:best,r_30,w_1465/v1779224226/IMG_1121_gn3t07.jpg"
              alt="Munachimso Zoe Maxwell"
              style={{
                width: 180,
                height: 180,
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
                filter: "brightness(0.97)",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling)
                  e.target.nextSibling.style.display = "flex";
              }}
            />
            {/* Fallback shown if image fails */}
            <div
              style={{
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: `${G}15`,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke={G}
                strokeWidth="1.2"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              <span
                style={{
                  color: G,
                  fontSize: "0.65rem",
                  letterSpacing: "0.15em",
                }}
              >
                PHOTO
              </span>
            </div>
          </div>
        </div>

        {/* Scripture */}
        <div className="max-w-sm mx-auto mb-10 px-2">
          <p
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "#7a6645",
              fontStyle: "italic",
              lineHeight: 1.75,
            }}
            className="text-base sm:text-lg"
          >
            "Let the little children come to me, and do not hinder them, for the
            kingdom of heaven belongs to such as these."
          </p>
          <p
            style={{ color: G, letterSpacing: "0.18em" }}
            className="text-xs uppercase mt-3"
          >
            — Matthew 19:14
          </p>
        </div>

        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-3 max-w-xs mx-auto mb-12">
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(90deg,transparent,${G})`,
            }}
          />
          <span style={{ color: G }}>❧</span>
          <span style={{ color: G }} className="text-lg">
            ✦
          </span>
          <span style={{ color: G }}>❧</span>
          <div
            style={{
              flex: 1,
              height: 1,
              background: `linear-gradient(90deg,${G},transparent)`,
            }}
          />
        </div>

        {/* ── COUNTDOWN ── */}
        {timeLeft ? (
          <div className="max-w-lg mx-auto mb-14">
            <p
              style={{ letterSpacing: "0.25em", color: "#9a8060" }}
              className="text-xs uppercase mb-5"
            >
              Celebrating in
            </p>
            <div className="grid grid-cols-4 gap-3 sm:gap-4">
              {[
                { label: "Days", value: timeLeft.days },
                { label: "Hours", value: timeLeft.hours },
                { label: "Mins", value: timeLeft.minutes },
                { label: "Secs", value: timeLeft.seconds },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{ background: NAVY, border: `1px solid ${G}50` }}
                  className="rounded-2xl py-5 flex flex-col items-center shadow-lg"
                >
                  <span
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: G,
                    }}
                    className="text-3xl sm:text-4xl font-bold tabular-nums"
                  >
                    {String(value).padStart(2, "0")}
                  </span>
                  <span
                    style={{ color: "#7a6645", letterSpacing: "0.15em" }}
                    className="text-xs uppercase mt-2"
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p
            style={{ color: G, fontFamily: "'Playfair Display', serif" }}
            className="text-2xl italic mb-14"
          >
            Today is the day! 🎉
          </p>
        )}

        {/* ── EVENT DETAILS ── */}
        <div className="max-w-md mx-auto space-y-3 mb-10">
          {[
            {
              label: "Date & Time",
              primary: "Sunday, 28 June 2026",
              secondary: "11:00 — 13:00 (Church Service)",
              tertiary: "2:00 PM — 5:00 PM (Party)",
            },
            {
              label: "Venue",
              primary: "Steinkjer Frikirke",
              secondary: "Ølvegata 24, 7715 Steinkjer",
            },
            { label: "Hosted by", primary: "Emmanuel and Blessing Maxwell" },
          ].map(({ label, primary, secondary, tertiary }) => (
            <div
              key={label}
              style={{
                background: "rgba(255,255,255,0.88)",
                borderColor: `${G}35`,
              }}
              className="rounded-2xl border shadow-sm p-5 text-left flex gap-4 items-start"
            >
              <div
                style={{
                  width: 3,
                  minHeight: 44,
                  borderRadius: 2,
                  background: `linear-gradient(180deg,${G},${G}80)`,
                  flexShrink: 0,
                }}
              />
              <div>
                <p
                  style={{ color: G, letterSpacing: "0.2em" }}
                  className="text-xs uppercase mb-1"
                >
                  {label}
                </p>
                <p
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: NAVY,
                  }}
                  className="font-semibold text-base"
                >
                  {primary}
                </p>
                {secondary && (
                  <p style={{ color: "#7a6645" }} className="text-sm mt-0.5">
                    {secondary}
                  </p>
                )}
                {tertiary && (
                  <p style={{ color: "#7a6645" }} className="text-sm mt-0.5">
                    {tertiary}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Guest count badge */}
        <div
          style={{ background: `${G}12`, border: `1px solid ${G}40` }}
          className="inline-flex items-center gap-3 rounded-full px-6 py-3 mb-16"
        >
          <span
            style={{ color: G, fontFamily: "'Playfair Display', serif" }}
            className="font-bold text-xl"
          >
            {stats.totalGuests}
          </span>
          <span style={{ color: "#7a6645" }} className="text-sm">
            {stats.totalGuests === 1 ? "guest" : "guests"} confirmed so far
          </span>
        </div>
      </div>

      {/* Bottom ornament */}
      <div className="text-center pb-3">
        <div
          style={{
            height: 1,
            background: `linear-gradient(90deg,transparent,${G}35,transparent)`,
          }}
        />
      </div>

      {/* ── ADMIN ── */}
      <div className="text-center pb-12 px-4 pt-4">
        <button
          onClick={() => setAdminOpen(!adminOpen)}
          style={{ color: `${G}45` }}
          className="text-xs hover:opacity-60 transition-opacity"
        >
          ···
        </button>

        {adminOpen && !adminUnlocked && (
          <div className="mt-3 max-w-xs mx-auto">
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="Admin password"
                value={adminPass}
                onChange={(e) => {
                  setAdminPass(e.target.value);
                  setAdminError(false);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleAdminUnlock()}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none"
              />
              <button
                onClick={handleAdminUnlock}
                className="bg-gray-700 hover:bg-gray-800 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                Go
              </button>
            </div>
            {adminError && (
              <p className="text-red-400 text-xs mt-2">Incorrect password.</p>
            )}
          </div>
        )}

        {adminUnlocked && (
          <div className="max-w-2xl mx-auto mt-6">
            <div className="bg-gray-900 rounded-2xl p-6 text-left">
              <div className="flex flex-wrap justify-between items-center mb-5 gap-3">
                <h3 className="text-white font-semibold text-lg">
                  RSVP Responses ({rsvps.length})
                </h3>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-400">
                      ✓ {stats.attending} attending
                    </span>
                    <span className="text-yellow-400">
                      ? {stats.maybe} maybe
                    </span>
                    <span className="text-red-400">
                      ✗ {stats.notAttending} declining
                    </span>
                  </div>
                  <button
                    onClick={() => exportToCSV(rsvps)}
                    disabled={rsvps.length === 0}
                    style={{
                      background: `linear-gradient(135deg,#c9a55a,#a07838)`,
                    }}
                    className="text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    ↓ Export CSV
                  </button>
                  <button
                    onClick={handleSendReminders}
                    disabled={sendingReminders || rsvps.filter((r) => r.attending === "yes" && r.email).length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {sendingReminders ? "Sending…" : "✉ Send Reminders"}
                  </button>
                </div>
              </div>
              <div className="bg-gray-800 rounded-xl px-5 py-3 mb-5 flex items-center gap-3">
                <span className="text-amber-400 text-2xl font-bold">
                  {stats.totalGuests}
                </span>
                <span className="text-gray-400 text-sm">
                  total guests expected (confirmed yes)
                </span>
              </div>
              {reminderStatus && (
                <p className="text-green-400 text-xs mb-4 text-center">
                  {reminderStatus}
                </p>
              )}
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {rsvps.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    No RSVPs yet.
                  </p>
                ) : (
                  rsvps.map((r) => (
                    <div key={r.id} className="bg-gray-800 rounded-xl p-4">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">{r.name}</p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {r.email}
                          </p>
                          {r.allergy && (
                            <p className="text-amber-300 text-xs mt-1.5">
                              Allergy: {r.allergy}
                            </p>
                          )}
                          {r.message && (
                            <p className="text-gray-300 text-sm mt-2 italic">
                              "{r.message}"
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${r.attending === "yes" ? "bg-green-900 text-green-300" : r.attending === "no" ? "bg-red-900 text-red-300" : "bg-yellow-900 text-yellow-300"}`}
                          >
                            {r.attending === "yes"
                              ? `✓ Yes · ${r.guests} guest${Number(r.guests) > 1 ? "s" : ""}`
                              : r.attending === "no"
                                ? "✗ Not attending"
                                : "? Maybe"}
                          </span>
                          {r.attending !== "no" && (
                            <p className="text-gray-500 text-xs mt-1">
                              {r.session === "church"
                                ? "Church only"
                                : r.session === "party"
                                  ? "Party only"
                                  : "Church & Party"}
                            </p>
                          )}
                          <p className="text-gray-600 text-xs mt-1.5">
                            {new Date(r.submittedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom gold rule */}
      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)`,
        }}
      />
    </div>
  );
}

export default BaptismInvite;
